import { StripeWordmark } from 'pages/Subscriptions/SubscriptionInner/PaymentBrandIcons';
import { Headline } from 'pages/UserProfile/Header/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ReactComponent as RocketLaunchIcon } from 'static/images/rocket_launch.svg';
import { Profile } from 'types';

import { TRIAL_DEFAULTS } from './constants';
import { IconCalendar, IconLock } from './icons';
import InvoiceHistory from './InvoiceHistory';
import PaymentDetailsCard from './PaymentDetailsCard';
import PlanFeatureList from './PlanFeatureList';
import StartTrialControl from './StartTrialControl';
import {
  ActionRow,
  Badge,
  Card,
  CardHeaderRow,
  CardSubtitle,
  FeatureBlurb,
  FeatureBox,
  FooterNote,
  FooterStripeNote,
  HeroAccent,
  HeroActions,
  HeroCard,
  HeroInner,
  HeroLeft,
  HeroSubtitle,
  HeroTitle,
  IconBox,
  PlanFooter,
  PlanName,
  TextLink,
  TrustRow,
  TwoColGrid,
} from './styled';
import UsageMetersCard from './UsageMetersCard';
import type { SubscriptionProfileModel } from './useSubscriptionProfileModel';

type FreeAccountViewProps = {
  model: SubscriptionProfileModel;
  profile: Profile | null | undefined;
  trialInlineError: string | null;
  changePlanPath: string;
};

const FreeAccountView = ({
  model,
  profile,
  trialInlineError,
  changePlanPath,
}: FreeAccountViewProps) => {
  const { t } = useTranslation();

  const paymentTrustFooter = (
    <FooterStripeNote>
      <IconLock size={16} />
      <span>{t('subscriptions.profile.trust.poweredBy')}</span>
      <StripeWordmark height={16} />
    </FooterStripeNote>
  );

  return (
    <>
      <HeroCard>
        <HeroAccent />
        <HeroInner>
          <HeroLeft>
            <IconBox $filled>
              <RocketLaunchIcon width={22} height={22} aria-hidden />
            </IconBox>
            <div>
              <HeroTitle>{t('subscriptions.profile.hero.title')}</HeroTitle>
              <HeroSubtitle>
                {t('subscriptions.profile.hero.subtitle')}
              </HeroSubtitle>
            </div>
          </HeroLeft>
          <HeroActions>
            <TextLink as={Link} to={changePlanPath}>
              {t('subscriptions.profile.hero.seeIncluded')}
            </TextLink>
            {!profile?.trialStarted ? (
              <StartTrialControl errorMessage={trialInlineError} />
            ) : null}
          </HeroActions>
        </HeroInner>
      </HeroCard>

      <TwoColGrid>
        <Card>
          <CardHeaderRow>
            <div>
              <Headline>{t('subscriptions.profile.plan.title')}</Headline>
              <CardSubtitle>
                {t('subscriptions.profile.freeAccount.subtitle')}
              </CardSubtitle>
            </div>
            <Badge $tone="slate">
              {t('subscriptions.profile.freeAccount.badge')}
            </Badge>
          </CardHeaderRow>
          <PlanName $muted>
            {t('subscriptions.profile.freeAccount.noActivePlan')}
          </PlanName>
          <FeatureBox>
            <FeatureBlurb>
              {t('subscriptions.profile.freeAccount.trialUnlockBlurb')}
            </FeatureBlurb>
            <PlanFeatureList
              maxCommunities={TRIAL_DEFAULTS.maxCommunities}
              maxGroups={TRIAL_DEFAULTS.maxGroups}
              maxMembers={TRIAL_DEFAULTS.maxMembers}
              maxStorage={TRIAL_DEFAULTS.maxStorage}
            />
          </FeatureBox>
          <PlanFooter>
            <FooterNote>
              <IconCalendar />
              {t('subscriptions.profile.freeAccount.trialNoteFooter')}
            </FooterNote>
            <ActionRow>
              {!profile?.trialStarted ? (
                <StartTrialControl errorMessage={trialInlineError} />
              ) : null}
              <TextLink as={Link} to={changePlanPath}>
                {t('subscriptions.profile.freeAccount.comparePlans')}
              </TextLink>
            </ActionRow>
          </PlanFooter>
        </Card>
        <PaymentDetailsCard
          subscription={model.subscription}
          isInvoiceBillingLayout={model.isInvoiceBillingLayout}
          showBillingPaymentDetails={model.showBillingPaymentDetails}
          showSwitchBillingMode={model.showSwitchBillingMode}
          isChfCurrency={model.isChfCurrency}
          canUseManageBilling={model.canUseManageBilling}
          billingPortalUrl={model.billingPortalUrl}
          billingCyclePrimaryText={model.billingCyclePrimaryText}
          translatedPaymentCycle={model.translatedPaymentCycle}
          paymentMethodLine={model.paymentMethodLine}
          cardExpiry={model.cardExpiry}
          lastInvoiceMeta={model.lastInvoiceMeta}
          lastInvoicePdfUrl={model.lastInvoicePdfUrl}
          invoiceContactEmail={model.invoiceContactEmail}
          invoicePayerEmails={model.invoicePayerEmails}
          invoiceDefaultEmail={model.invoiceDefaultEmail}
          onViewInvoices={model.scrollToInvoiceHistory}
        />
      </TwoColGrid>

      <UsageMetersCard
        locked
        changePlanPath={changePlanPath}
        isSubscriptionOwner={model.isSubscriptionOwner}
        maxMembers={model.maxUsers}
        maxCommunities={model.maxCommunities}
        maxGroups={model.maxGroups}
        maxStorage={model.maxBucketSize}
        usedMembers={model.usedMembers}
        usedCommunities={model.usedCommunities}
        usedGroups={model.usedGroups}
        usedStorage={model.usedStorage}
      />
      {model.isChfCurrency ? <InvoiceHistory /> : null}

      <TrustRow>{paymentTrustFooter}</TrustRow>
    </>
  );
};

export default FreeAccountView;
